import clsx from 'clsx';
import { Link, RawHtml, Typography } from '~/shared/components';
import { useTranslation } from '~/shared/utils/translation';
import styles from './ProductSpecifications.module.scss';

export interface ProductSpecification {
    name?: string;
    text?: string;
    url?: string;
    target?: string;
    id?: string;
    itemProp?: string;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    specifications: ProductSpecification[];
    /** The market's GPSR safety statement, rendered as a final row. */
    gpsrText?: string;
}

const replaceSemi = (text: string) => text?.replace(/;/g, ', ');

// Only add space if the comma is a list divider and NOT a unit value.
const addSpaceAfterComma = (text: string) =>
    text
        .replace(new RegExp(/(\D),(\D)/, 'g'), '$1, $2')
        .replace(new RegExp(/(\d),(\D)/, 'g'), '$1, $2')
        .replace(new RegExp(/(\D),(\d)/, 'g'), '$1, $2');

const formatDisplayValue = (text?: string) => {
    if (!text) return '';

    if (text.includes(';')) return replaceSemi(text);

    if (text.includes(',')) return addSpaceAfterComma(text);

    return text;
};

export const ProductSpecifications: React.FC<Props> = ({
    className,
    specifications,
    gpsrText,
    ...rest
}) => {
    const { translate } = useTranslation();

    return (
        <div className={clsx(styles.productSpecifications, className)} {...rest}>
            {specifications.map((item) => (
                <dl className={styles.specification} key={item?.id || item?.name}>
                    <Typography as="dt" variant="paragraph-M" className={styles.specificationName}>
                        {item.name}
                    </Typography>

                    <Typography
                        as="dd"
                        variant="paragraph-M"
                        className={styles.specificationValue}
                        itemProp={item.itemProp}
                    >
                        {item.url ? (
                            <Link
                                {...item}
                                className={styles.specificationLink}
                                title={formatDisplayValue(item.text)}
                                href={item.url}
                            >
                                {formatDisplayValue(item.text)}
                            </Link>
                        ) : (
                            formatDisplayValue(item.text)
                        )}
                    </Typography>
                </dl>
            ))}

            {gpsrText && (
                <dl className={styles.specification}>
                    <Typography as="dt" variant="paragraph-M" className={styles.specificationName}>
                        {translate('pdp.gsprLabel')}
                    </Typography>

                    <Typography as="dd" variant="paragraph-M" className={styles.specificationValue}>
                        <RawHtml html={gpsrText} shouldSanitizeHtml />
                    </Typography>
                </dl>
            )}
        </div>
    );
};
